/**
 * App — Entry Point
 *
 * Imports every ES6 module and bootstraps them once the DOM is ready.
 * This file must be loaded with <script type="module">.
 *
 * @module App
 */

import Navigation from './modules/Navigation.js';
import ScrollAnimations from './modules/ScrollAnimations.js';
import InteractiveEffects from './modules/InteractiveEffects.js';
import ContactForm from './modules/ContactForm.js';
import ContactTunnel from './modules/ContactTunnel.js';
import CounterAnimation from './modules/CounterAnimation.js';
import LottieLoader from './modules/LottieLoader.js';
import PortfolioCarousel from './modules/PortfolioCarousel.js';
import MobileMenu from './modules/MobileMenu.js';
import LeadForm from './modules/LeadForm.js';
import Estimateur from './modules/Estimateur.js';
// import RefontePopup from './modules/RefontePopup.js';

class App {

  constructor() {
    this.modules = {};
  }

  /**
   * Instantiate each module. Modules that target optional markup
   * (forms, lottie containers) are only created when the relevant
   * DOM nodes exist on the page.
   */
  init() {
    // Always initialised — Navigation & scroll effects are global.
    this.modules.navigation = new Navigation();
    this.modules.scrollAnimations = new ScrollAnimations();

    // Interactive hover effects — global (self-guards on touch devices).
    this.modules.interactiveEffects = new InteractiveEffects();

    // Contact tunnel — only when the tunnel is present.
    if (document.querySelector(ContactTunnel.DEFAULTS.selectors.tunnel)) {
      this.modules.contactTunnel = new ContactTunnel();
    }

    // Contact form — only when the form is present.
    if (document.querySelector(ContactForm.DEFAULTS.selectors.form)) {
      this.modules.contactForm = new ContactForm();
    }

    // Counter animations — only when counters are present.
    if (document.querySelectorAll(CounterAnimation.DEFAULTS.selector).length) {
      this.modules.counterAnimation = new CounterAnimation();
    }

    // Portfolio carousel — lazy-init via IntersectionObserver. Swiper pull
    // 34 modules ESM depuis jsdelivr → ne pas les charger tant que l'user
    // n'a pas scrollé près du carrousel (économise ~1s de LCP mobile).
    const swiperEl = document.querySelector(PortfolioCarousel.DEFAULTS.selector);
    if (swiperEl) {
      if ('IntersectionObserver' in window) {
        const io = new IntersectionObserver((entries, observer) => {
          if (entries[0].isIntersecting) {
            this.modules.portfolioCarousel = new PortfolioCarousel();
            observer.disconnect();
          }
        }, { rootMargin: '400px' });
        io.observe(swiperEl);
      } else {
        this.modules.portfolioCarousel = new PortfolioCarousel();
      }
    }

    // Lottie animations — only when containers are present.
    if (document.querySelectorAll(LottieLoader.DEFAULTS.selector).length) {
      this.modules.lottieLoader = new LottieLoader();
    }

    // Mobile menu enhancements — logo + CTA in overlay.
    this.modules.mobileMenu = new MobileMenu();

    // Lead form — mini "devis gratuit" on landing pages.
    if (document.querySelector(LeadForm.DEFAULTS.selector)) {
      this.modules.leadForm = new LeadForm();
    }

    // Estimateur — interactive price calculator.
    if (document.querySelector(Estimateur.DEFAULTS.selector)) {
      this.modules.estimateur = new Estimateur();
    }

    // Refonte popup — disabled (site live).
    // this.modules.refontePopup = new RefontePopup();
  }

  /**
   * Tear down every active module (useful for SPA-like page
   * transitions or hot-reload scenarios).
   */
  destroy() {
    Object.values(this.modules).forEach((mod) => {
      if (mod && typeof mod.destroy === 'function') {
        mod.destroy();
      }
    });

    this.modules = {};
  }
}

/* ------------------------------------------------------------------
 * Bootstrap
 * ----------------------------------------------------------------*/

const app = new App();

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => app.init());
} else {
  // DOM already parsed (e.g. script is deferred or at end of body).
  app.init();
}

export default app;
